import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Plus, Archive, Eye, RefreshCw, Info } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';
import { listCharts } from './api';
import { ParticipantChartList } from './ParticipantChartList';
import { ParticipantChartEmptyState } from './ParticipantChartEmptyState';
import { ParticipantChartCreateModal } from './ParticipantChartCreateModal';
import { BowelChartComplianceWidget } from './BowelChartComplianceWidget';

export function ParticipantChartsTab({ participantId }) {
  const { user } = useAuth();
  const perms = user?.permissions || [];
  const canView = perms.includes('participant_charts.view');
  const canCreate = perms.includes('participant_charts.create');
  const canArchive = perms.includes('participant_charts.archive');

  const [charts, setCharts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const fetchCharts = useCallback(async () => {
    if (!canView) return;
    try {
      setLoading(true);
      const rows = await listCharts(participantId, showArchived ? {} : { status: 'active' });
      setCharts(rows);
    } catch (e) {
      const msg = e?.response?.data?.error?.message || 'Failed to load charts';
      toast.error(msg);
      setCharts([]);
    } finally {
      setLoading(false);
    }
  }, [participantId, showArchived, canView]);

  useEffect(() => { fetchCharts(); }, [fetchCharts]);

  if (!canView) {
    return (
      <div className="py-8 text-center text-sm text-muted-foreground" data-testid="charts-no-access">
        You don't have permission to view charts for this participant.
      </div>
    );
  }

  const activeCount = charts.filter(c => c.status === 'active').length;
  const archivedCount = charts.filter(c => c.status === 'archived').length;

  return (
    <div className="space-y-4" data-testid="participant-charts-tab">
      <BowelChartComplianceWidget participantId={participantId} />
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <CardTitle className="text-base">Charts</CardTitle>
              {loading ? (
                <Badge variant="outline" className="text-[10px] animate-pulse">Loading…</Badge>
              ) : (
                <>
                  <Badge variant="outline" className="text-[10px] bg-emerald-50 border-emerald-300 text-emerald-700" data-testid="charts-active-count">
                    {activeCount} active
                  </Badge>
                  {archivedCount > 0 && (
                    <Badge variant="outline" className="text-[10px] bg-slate-100 border-slate-300 text-slate-600" data-testid="charts-archived-count">
                      {archivedCount} archived
                    </Badge>
                  )}
                </>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowArchived(v => !v)}
                className="h-8 text-xs"
                data-testid="charts-toggle-archived"
              >
                {showArchived ? <Eye className="w-3.5 h-3.5 mr-1" /> : <Archive className="w-3.5 h-3.5 mr-1" />}
                {showArchived ? 'Hide archived' : 'Show archived'}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={fetchCharts}
                className="h-8 text-xs"
                data-testid="charts-refresh"
              >
                <RefreshCw className="w-3.5 h-3.5 mr-1" />Refresh
              </Button>
              {canCreate && (
                <Button
                  size="sm"
                  onClick={() => setCreateOpen(true)}
                  className="h-8 text-xs"
                  data-testid="charts-create-btn"
                >
                  <Plus className="w-3.5 h-3.5 mr-1" />Create chart
                </Button>
              )}
            </div>
          </div>
          <p className="text-xs text-muted-foreground pt-1 flex items-start gap-1">
            <Info className="w-3 h-3 mt-0.5 shrink-0" />
            Structured clinical charts per participant. Open a chart to record and review entries.
          </p>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-8 text-sm text-muted-foreground text-center" data-testid="charts-loading">
              Loading charts…
            </div>
          ) : charts.length === 0 ? (
            <ParticipantChartEmptyState canCreate={canCreate} onCreate={() => setCreateOpen(true)} />
          ) : (
            <ParticipantChartList
              charts={charts}
              participantId={participantId}
              canArchive={canArchive}
              onChanged={fetchCharts}
            />
          )}
        </CardContent>
      </Card>

      <ParticipantChartCreateModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        participantId={participantId}
        onCreated={fetchCharts}
      />
    </div>
  );
}
